<template>
    <v-dialog 
        :value="show"
        fullscreen 
        hide-overlay
        @keydown.esc="close"
        transition="dialog-bottom-transition">
        <v-card>
            <v-toolbar dark color="primary">
                <v-btn 
                    icon 
                    dark 
                    :disabled="saving" 
                    @click="close">
                    <v-icon large>close</v-icon>
                </v-btn>
                <v-spacer></v-spacer>
                <v-toolbar-title>
                    <span v-if="originalItem">Edit Unit</span>
                    <span v-else>Add New Unit</span>
                </v-toolbar-title>
                <v-spacer></v-spacer>
            </v-toolbar>
            <v-layout row align-center justify-center fill-height v-if="loading">
                <v-flex shrink>
                    <v-progress-circular indeterminate></v-progress-circular>
                </v-flex>
            </v-layout>
            <v-form v-else ref="editForm" v-model="editValid">
                <v-layout class="dialog-content">
                    <v-flex>
                        <v-layout row>
                            <v-flex xs6>
                                <v-container fluid>
                                    <v-layout row wrap>
                                        <v-flex xs12>
                                            <v-autocomplete
                                                label="Unit Description"
                                                :items="units"
                                                item-value="UnitID"
                                                item-text="Name"
                                                v-model="editedItem.UnitID"
                                                :disabled="editedItem.PlantUnitID > 0"
                                                :rules="[rules.requiredObject]"
                                                @change="loadData">
                                            </v-autocomplete>
                                        </v-flex>
                                        <v-flex xs12>
                                            <v-text-field 
                                                label="Pack Size (Kg)"
                                                v-model="editedItem.PackSize"
                                                :rules="[decimal]"                                                
                                                @keydown.enter="packSizeChanged"
                                                @blur="packSizeChanged"                                     
                                            >
                                            </v-text-field>
                                        </v-flex>
                                        <v-flex xs12>
                                            <v-text-field 
                                                label="Tare Weight (Kg)"
                                                v-model="editedItem.TareKg"
                                                :rules="[decimal]"                                        
                                            >
                                            </v-text-field>
                                        </v-flex>
                                        <v-flex xs12>
                                            <v-text-field 
                                                label="Min. Target (Kg)"
                                                v-model="editedItem.MinimumTargetKg"
                                                :rules="[decimal]"
                                            >
                                            </v-text-field>
                                        </v-flex>
                                        <v-flex xs12>
                                            <v-text-field 
                                                label="Max. Target (Kg)"
                                                v-model="editedItem.MaximumTargetKg"
                                                :rules="[decimal]"
                                            >
                                            </v-text-field>
                                        </v-flex>
                                        <v-flex xs12>
                                            <v-text-field 
                                                label="Ave Kgs per Unit"
                                                v-model="editedItem.AveKgsPerUnit"
                                                :rules="[decimal]"
                                                @keydown.enter="avgKgsOrNumInTrayChanged"
                                                @blur="avgKgsOrNumInTrayChanged"
                                            >
                                            </v-text-field>
                                        </v-flex>                                    
                                        <v-flex xs12>
                                            <v-autocomplete
                                                label="Recording Mode"
                                                :items="recordingModes"
                                                item-value="RecordingModeID"
                                                item-text="Name"
                                                v-model="editedItem.RecordingModeID"
                                                :rules="[rules.requiredObject]"
                                                >
                                            </v-autocomplete>
                                        </v-flex>
                                        <v-flex xs12>
                                            <v-text-field 
                                                label="# in Tray"
                                                v-model="editedItem.NumInTray"
                                                :rules="[integer]"
                                                @keydown.enter="avgKgsOrNumInTrayChanged"
                                                @blur="avgKgsOrNumInTrayChanged"
                                            >
                                            </v-text-field>
                                        </v-flex>
                                        <v-flex xs12>
                                            <v-text-field 
                                                label="Tray Weight (Kg)"
                                                v-model="editedItem.TrayWeight"
                                                :rules="[decimal]"
                                            >
                                            </v-text-field>
                                        </v-flex>
                                        <v-flex xs12>
                                            <v-checkbox
                                                v-model="editedItem.IsActive"
                                                label="Active"
                                            >
                                            </v-checkbox>
                                        </v-flex>
                                    </v-layout>
                                </v-container>
                            </v-flex>
                            <v-divider vertical></v-divider>
                            <v-flex xs6>
                                <v-container fluid>
                                    <v-layout justify-center wrap>
                                        <v-flex xs12 pb-3>
                                            <v-toolbar flat>
                                                <v-toolbar-title>Applies to Products / Varieties</v-toolbar-title>
                                            </v-toolbar>                                            
                                        </v-flex>
                                        <v-flex shrink>                                            
                                            <v-text-field
                                                v-model="search"
                                                label="Filter"
                                                outline                                                                                        
                                                hide-details
                                                clearable                                            
                                            ></v-text-field>
                                            <div class="scroll-here">
                                                <v-treeview                                                                                                                                                     
                                                    :items="items"                                                
                                                    :search="search"                                                                                                    
                                                    expand-icon="arrow_downward"
                                                    on-icon="check_box"
                                                    off-icon="check_box_outline_blank"
                                                    indeterminate-icon="indeterminate_check_box"
                                                    >
                                                    <template v-slot:append="{ item }">
                                                        <v-layout row justify-end fill-height align-center pl-4>
                                                            <v-flex shrink>
                                                                <v-chip v-if="item.children" :color="item.children.filter(c => c.selected).length > 0 ? 'primary' : 'grey'" text-color="white">{{ item.children.filter(c => c.selected).length }}</v-chip>
                                                            </v-flex>
                                                            <v-flex shrink>
                                                                <v-checkbox class="pt-0 mt-0" label="All" hide-details v-if="item.children" v-model="item.all" @change="setAll(item, $event)">
                                                                </v-checkbox>
                                                            </v-flex>
                                                            <v-flex shrink>
                                                                <v-checkbox class="pt-0 mt-0 ml-1" label="Unit is default?" :disabled="!item.children.some(c=> c.selected)" hide-details v-if="item.children" v-model="item.default" @change="setDefault(item, $event, false)">
                                                                </v-checkbox>
                                                            </v-flex>
                                                        </v-layout>                                                        
                                                    </template>
                                                    <template v-slot:prepend="{ item }">
                                                        <v-checkbox :label="item.name" v-if="!item.children" :disabled="item.disabled" v-model="item.selected">
                                                        </v-checkbox>
                                                    </template>
                                                    <template v-slot:label="{ item }">                                                        
                                                        {{ item.children ? item.name : "" }}                                                        
                                                    </template>
                                                </v-treeview>
                                            </div>
                                        </v-flex>
                                    </v-layout>
                                </v-container>
                            </v-flex>
                        </v-layout>
                        <v-layout wrap class="dialog-action-buttons">
                            <v-flex xs12 sm6>
                                <v-btn 
                                    block 
                                    color="success" 
                                    :disabled="!editValid" 
                                    :loading="saving" 
                                    @click="save">
                                    <span>Save</span>
                                </v-btn>
                            </v-flex>
                            <v-flex xs12 sm6>
                                <v-btn 
                                    block 
                                    color="error" 
                                    :disabled="saving" 
                                    @click="close">
                                    <span>Cancel</span>
                                </v-btn>
                            </v-flex>
                        </v-layout>
                        <v-layout wrap>
                            <v-flex xs12>
                                <v-alert 
                                    :value="error" 
                                    outline 
                                    type="error">
                                    <span>{{ error }}</span>
                                </v-alert>
                            </v-flex>
                        </v-layout>
                    </v-flex>
                </v-layout>
            </v-form>
            <v-dialog v-model="confirmDefaultPlantUnitCategoryDialog" max-width="450" transition="dialog-bottom-transition" persistent @keydown.esc="confirmDefaultPlantUnitCategoryDialog = false">
                <v-card>
                    <v-toolbar dark color="primary">
                        <v-toolbar-title>Confirm Overwrite Default Unit</v-toolbar-title>
                    </v-toolbar>
                    <v-layout wrap>
                        <v-flex class="ma-1">
                            There is an existing default product category for the plant. Please confirm you want to overwrite it.
                        </v-flex>             
                    </v-layout>
                    <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn @click="confirmOverwrite" color="primary">Confirm</v-btn>
                        <v-btn @click="confirmDefaultPlantUnitCategoryDialog = false" color="error">Cancel</v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>
        </v-card>
    </v-dialog>
</template>

<script>
import plantConfigurationApi from "@/api/plantConfigurationApi";
import validationRules from "@/helpers/validationrules";
import BaseAPI from "@/api/baseApi";
import UtilService from '@/services/utilService.js';

export default {
    props: {
        plantID: {
            required: true,
            type: Number,
            default: null
        },
        originalItem: {
            required: false,
            type: Object,
            default: null
        },
        show: {
            required: true,
            type: Boolean,
            default: false
        },
        productCategories: {
            required: true,
            type: Array,
            default: null
        },
        units: {
            required: true,
            type: Array,
            default: null
        },
        recordingModes: {
            required: true,
            type: Array,
            default: null
        },
        plantUnits : {
            required: true,
            type: Array,
            default: null
        }
    },
    data() {
        return {
            rules: validationRules.rules,
            loading: false,
            editedItem: { ProductCategories:[], IsActive: true },
            items: [],
            saving: false,
            error: null,
            editValid: false,
            search: null,
            confirmDefaultPlantUnitCategoryDialog: false
        }
    },
    computed: {
    },    
    methods: {
        close(saved) {
            if(this.confirmDefaultPlantUnitCategoryDialog == true) {
                this.confirmDefaultPlantUnitCategoryDialog = false;
                return;
            }
            this.$refs.editForm.reset();
            this.$emit("close", saved);
        },
        async loadData() {
            try {
                if (!this.editedItem.UnitID) {
                    return;
                }

                this.loading = true;
                this.error = null;

                this.items = this.productCategories.map(pc => {
                    // Lets not include product categories if they are already used on another Plant Unit entry with the same Unit
                    let match = this.plantUnits.some(pu => 
                        pu.PlantUnitID !== this.editedItem.PlantUnitID &&
                        pu.UnitID == this.editedItem.UnitID &&
                        pu.ProductCategories.some(x => x.ProductCategoryID === pc.ProductCategoryID)
                        );

                    if (!match) {
                        let matchingProductCategory = this.editedItem.ProductCategories.find(x => x.ProductCategoryID === pc.ProductCategoryID);
                        return {
                            id: pc.ProductCategoryID,
                            name: pc.Name,
                            all: matchingProductCategory ? matchingProductCategory.AllVarieties : false,
                            default: matchingProductCategory ? matchingProductCategory.IsDefault : false,
                            children: pc.Varieties.map(v => {
                                return {
                                    id: `${v.ProductTypeID}-${v.ProductVarietyID}`,
                                    name: v.Description,
                                    selected: this.editedItem.ProductCategories.some(x => x.Varieties.some(y => y.ProductVarietyID === v.ProductVarietyID)),
                                    disabled: matchingProductCategory ? matchingProductCategory.AllVarieties : false,
                                    ProductVarietyID: v.ProductVarietyID,
                                    ProductTypeID: v.ProductTypeID
                                }
                            }).sort(UtilService.sortAscending(x => x.name))
                        }
                    }
                });

                this.items = this.items.filter(i => i !== undefined);
            }
            catch (err) {
                this.error = await BaseAPI.parseHttpRequestError(err);
                throw err;
            }
            finally {
                this.loading = false;
            }
        },
        async save() {
            try {
                this.saving = true;
                this.error = null;

                // Lets get all the selected items
                this.editedItem.ProductCategories = [];

                this.items.forEach(i => {
                    if (i.children.some(c => c.selected) || i.default) {
                        let productCategory = {
                            ProductCategoryID: i.id,                            
                            AllVarieties: i.all,
                            Varieties:[],
                            IsDefault: i.default
                        }

                        i.children.filter(c => c.selected).forEach(c => {
                            productCategory.Varieties.push({
                                ProductVarietyID: c.ProductVarietyID,
                                ProductTypeID: c.ProductTypeID
                            })
                        })
                                                
                        this.editedItem.ProductCategories.push(productCategory);
                    }
                });

                await plantConfigurationApi.savePlantUnit(this.plantID, this.editedItem);

                this.close(true);
            }
            catch (err) {
                this.error = await BaseAPI.parseHttpRequestError(err);
                throw err;
            }
            finally {
                this.saving = false;
            }
        },
        setAll(item, value) {
             item.children.forEach(c => { c.selected = value; c.disabled = value });
             item.all = value;
             if(value == false) {
                 item.default = false;
             }
        },
        setDefault(item, value, confirm) {
            var hasOtherDefaultForTheCategory = this.plantUnits.some(pu=> pu.ProductCategories.some(pc=> pc.ProductCategoryID == item.id && pc.IsDefault == true) && pu.PlantUnitID != this.editedItem.PlantUnitID);
            // If setting it as default, check all the plant units to see whether there already a default one, if yes, show confirmation dialog
            if(hasOtherDefaultForTheCategory && value == true && !confirm) {
                this.editedProductCategory = item;
                this.confirmDefaultPlantUnitCategoryDialog = true;
                
                this.$nextTick(()=> {
                    item.default = false;
                })
               
            } 
            else {
                this.editedProductCategory = null;
                this.confirmDefaultPlantUnitCategoryDialog = false;
                item.default = value;
            }
           
        },
        confirmOverwrite() {
            this.setDefault(this.editedProductCategory, true, true);
        },
        decimal(v){
            if(v == null || !v.toString().match(/^[0-9]{1,15}.?[0-9]{0,4}$/)){
                return "Please enter a number in the format of XXXXX.XXXX";
            }

            return false;
        },
        integer(v){
            if(v == null || !v.toString().match(/^[1-9]{1}?[0-9]{0,1}$/)){
                return "Please enter a number between 1 and 99";
            }

            return false;
        },
        packSizeChanged() {
            if (this.editedItem.PackSize >= 0) {
                // Default Values                        
                if (this.editedItem.MinimumTargetKg == null) {
                    this.$set(this.editedItem, "MinimumTargetKg", Math.round(this.editedItem.PackSize * 1.1 * 10000) / 10000);
                }

                if (this.editedItem.MaximumTargetKg == null) {
                    this.$set(this.editedItem, "MaximumTargetKg", Math.round(this.editedItem.PackSize * 1.1 * 10000) / 10000);
                }

                if (this.editedItem.AveKgsPerUnit == null) {
                    this.$set(this.editedItem, "AveKgsPerUnit", Math.round(this.editedItem.PackSize * 1.1 * 10000) / 10000);
                    this.avgKgsOrNumInTrayChanged();
                } 
            }
        },
        avgKgsOrNumInTrayChanged() {            
            if (this.editedItem.NumInTray >= 0 && this.editedItem.AveKgsPerUnit >= 0) {
                this.$set(this.editedItem, "TrayWeight", Math.round(this.editedItem.NumInTray * this.editedItem.AveKgsPerUnit * 10000) / 10000);
            }
        }
    },
    async mounted() {
        if (this.originalItem) {
            this.editedItem = JSON.parse(JSON.stringify(this.originalItem));
        } 

        await this.loadData();       
    },
}
</script>
<style scoped>
.scroll-here {
  overflow: auto;
  height: 540px;
  padding:10px;
}
</style>